import cn from 'classnames';
import { InputHTMLAttributes, KeyboardEvent, ReactNode } from 'react';

import style from './RadioTab.module.scss';

export interface RadioTabProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'content'> {
  // radio group value
  value: string;
  // input item value
  itemValue: string;
  group: string;
  onChange: (e?: any) => void;
  className?: string;
  containerClass?: string;
  activeContainerClass?: string;
  label: ReactNode | string;
  content?: ReactNode;
  show?: boolean;
  alwaysShowContent?: boolean;
  // to make the radio selectable by clicking anywhere in the element (including content)
  allClickable?: boolean;
  hideRadio?: boolean;
}

export default function RadioTab({
  value,
  itemValue,
  group,
  onChange,
  className,
  containerClass,
  activeContainerClass,
  label,
  content,
  show = true,
  alwaysShowContent = false,
  allClickable = false,
  hideRadio = false,
  ...inputProps
}: RadioTabProps) {
  const id = `${group}-${itemValue}`;
  const active = value === itemValue;
  const handleClick = () => {
    if (!active) {
      onChange();
    }
  };

  // imo, it's not ideal to have nested clickable elements, but it's still the request so :shrug:
  // adding `button` role here will trigger a11y error
  const clickableProps = allClickable
    ? {
        tabIndex: 0,
        onClick: handleClick,
        onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter') {
            handleClick();
          }
        },
      }
    : undefined;

  return (
    <div
      className={cn(style.item, containerClass, {
        [style.active]: active,
        [activeContainerClass || '']: active,
        'cursor-pointer': allClickable,
      })}
      {...clickableProps}
    >
      <label htmlFor={id} className={style.label}>
        <input
          id={id}
          className={cn(style.radio, { [style.hideRadio]: hideRadio }, className)}
          type="radio"
          name={group}
          value={itemValue}
          onChange={onChange}
          checked={active}
          {...inputProps}
        />
        {label}
      </label>
      {(alwaysShowContent || active) && show && content}
    </div>
  );
}
